import React from 'react';
import { BanknotesIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import type { Client } from '../../types';
import { Card } from '../ui';

type PlanRow = NonNullable<Client['plans']>[number] & {
  planTemplate?: { name?: string; description?: string; planType?: string };
  status?: string;
  finalPrice?: number;
  price?: number;
};

const TYPE_BADGE: Record<string, string> = {
  funeral: 'bg-violet-100 text-violet-900 ring-violet-200/80',
  memorial: 'bg-sky-100 text-sky-900 ring-sky-200/80',
  cremation: 'bg-orange-100 text-orange-900 ring-orange-200/80',
  default: 'bg-slate-100 text-slate-800 ring-slate-200/80'
};

function planStatusLabel(status: string): string {
  const s = status.toLowerCase();
  if (s === 'active') return 'Activo';
  if (s === 'inactive') return 'Suspendido';
  if (s === 'completed') return 'Finalizado';
  return status;
}

export interface ClientPlanSectionProps {
  client: Client;
  className?: string;
  /** Acciones futuras: cambiar plan, ver plantilla */
  showFooterActions?: boolean;
}

export const ClientPlanSection: React.FC<ClientPlanSectionProps> = ({
  client,
  className,
  showFooterActions = true
}) => {
  const plans = (client.plans || []) as PlanRow[];

  if (!plans.length) {
    return (
      <Card className={clsx('border-dashed border-slate-300 bg-slate-50/50 p-8 text-center', className)}>
        <ShieldCheckIcon className="mx-auto h-10 w-10 text-slate-300" aria-hidden />
        <p className="mt-2 font-semibold text-slate-700">Sin plan de servicio asignado</p>
        <p className="mt-1 text-sm text-slate-500">Asocie un plan desde la edición del cliente.</p>
      </Card>
    );
  }

  return (
    <div className={clsx('space-y-4', className)}>
      {plans.map((plan) => {
        const name = plan.planTemplate?.name || plan.name || 'Plan';
        const desc = plan.planTemplate?.description || plan.description || '';
        const pType = (plan.planTemplate?.planType || plan.planType || 'default').toLowerCase();
        const badge = TYPE_BADGE[pType] || TYPE_BADGE.default;
        const price = plan.finalPrice ?? plan.price ?? 0;
        const st = String(plan.status || 'active');

        return (
          <Card
            key={plan.id}
            className="overflow-hidden border-slate-200/90 bg-gradient-to-br from-white to-slate-50/50 shadow-md ring-1 ring-slate-100/90"
          >
            <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0 flex-1 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-lg font-bold text-slate-900">{name}</h3>
                  <span className={clsx('rounded-lg px-2.5 py-0.5 text-xs font-bold ring-1 ring-inset', badge)}>
                    {plan.planTemplate?.planType || plan.planType || 'Plan'}
                  </span>
                  <span className="rounded-lg bg-emerald-50 px-2.5 py-0.5 text-xs font-bold text-emerald-900 ring-1 ring-emerald-200/80">
                    {planStatusLabel(st)}
                  </span>
                </div>
                {desc && <p className="text-sm leading-relaxed text-slate-600">{desc}</p>}
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1 rounded-xl bg-slate-900 px-4 py-3 text-white">
                <div className="flex items-center gap-1.5 text-xs font-medium text-slate-300">
                  <BanknotesIcon className="h-4 w-4" aria-hidden />
                  Precio / cuota
                </div>
                <p className="text-2xl font-bold tabular-nums">
                  ${Number(price).toLocaleString('es', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
            {showFooterActions && (
              <div className="flex flex-wrap gap-2 border-t border-slate-100 bg-slate-50/80 px-5 py-3">
                <span className="text-xs font-medium text-slate-500">
                  Próximamente: cambiar plan, ver detalle de plantilla y generar documentos.
                </span>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
};
